import 'tippy.js/dist/tippy.css';
import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faMagnifyingGlass } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { styled, theme } from '@components-shared/src/stitches';
import { getSearchPageResults } from '@components/Explore/helpers';
import { SearchResults } from '@components/SearchBar/SearchResults';
import {
  ClearSearchButton,
  SearchBarContainer,
  SearchInput,
  SearchInputContainer,
} from '@components/main-components/search/SearchInput';
import { useMediaQuery } from '@context/mediaQuery';
import { ParamsStore } from '@context/params';
import { usePrimarySearchResults } from '@hooks/search/usePrimarySearchResults';
import { globalSearchSoundState, useSearchInputNew } from '@hooks/search/useSearchInputNew';
import { useListenElementOffsetWidth } from '@hooks/useListenElementWidth';
import { useHeaderStore } from '@store/header';
import { BLUR_BACKGROUND_PATHS } from '@utils/constants';
import { LazyTippy } from '../LazyTippy';

/*#__PURE__*/ gql(/* GraphQL */ `
  query InitialPrimarySearchResults {
    releases(
      pagination: { first: 3, sort: { createdAt: DESC } }
      filter: { releaseAlbumRevealStatus: ONLY_NOT_REVEALED_ALBUMS }
    ) {
      edges {
        node {
          ...SearchResultRelease
        }
      }
    }
    artists(pagination: { first: 3, sort: DESC }, filter: { hasRelease: true }) {
      edges {
        node {
          id
          ...SearchResultArtist
        }
      }
    }
    allCollectors(
      input: {
        filter: { includeArtists: false, onlyWithUsername: true }
        pagination: { first: 3, sort: { createdAt: DESC } }
      }
    ) {
      edges {
        node {
          id
          ...SearchResultCollector
        }
      }
    }
    allShelves(
      input: {
        filter: { type: [USER_CREATED] }
        pagination: { first: 3, sort: { createdAt: DESC } }
      }
    ) {
      edges {
        node {
          ...SearchResultShelf
        }
      }
    }
  }

  query PrimarySearchResults($input: SearchInput!) {
    search(input: $input) {
      id
      releasesPaginated {
        edges {
          node {
            ...SearchResultRelease
          }
        }
      }
      artistsPaginated {
        edges {
          node {
            ...SearchResultArtist
          }
        }
      }
      shelves(pagination: { first: 10 }) {
        edges {
          node {
            id
            ...SearchResultShelf
          }
        }
      }
      collectors(pagination: { first: 10 }) {
        edges {
          node {
            id
            ...SearchResultCollector
          }
        }
      }
    }
  }
  fragment SearchResultRelease on Release {
    id
    title
    webappUri
    track {
      id
    }
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    artist {
      id
      name
      soundHandle
    }
    mintStartTimestamp
    quantityUpperBound
    quantityLowerBound
    supplyCutoffTimestamp
    finalQuantity
    ...ReleaseCardActions
    ...ReleasesCardInfo
    ...ReleaseUpdates
  }
  fragment SearchResultArtist on Artist {
    id
    name
    webappUri
    user {
      id
      publicAddress
      avatar {
        id
        url
        dominantColor
      }
    }
  }
  fragment SearchResultShelf on Shelf {
    id
    name
    webappUri
    coverReleases {
      id
      coverImage {
        id
        url
      }
      staticCoverImage {
        id
        url
      }
    }
    user {
      id
      username
      artist {
        id
      }
      ...ShelfCreator
    }
    ...PlaylistCardDetails
    ...PlaylistCoverImages
  }
  fragment SearchResultCollector on User {
    id
    username
    publicAddress
    webappUri
    avatar {
      id
      url
      dominantColor
    }
  }
`);

export function SearchBar({
  extraClearSearch,
  hideClearOnEmpty = true,
  onSearchInputFocus,
  onSearchInputBlur,
}: {
  extraClearSearch?: (args: { inputRef: React.RefObject<HTMLInputElement> }) => void;
  hideClearOnEmpty?: boolean;
  onSearchInputFocus?: () => void;
  onSearchInputBlur?: () => void;
}) {
  const { close, isSearchOpen } = useHeaderStore();
  const { pathname } = useRouter();
  const { tablet } = useMediaQuery();

  const {
    focus,
    input,
    focusOn,
    focusOff,
    barInputText,
    handleInputChange,
    clearText,
    barSearchText,
    hasHadFocus,
  } = useSearchInputNew({ searchState: globalSearchSoundState });

  const {
    artistResults = [],
    releaseResults = [],
    playlistResults = [],
    collectorResults = [],
    isLoading,
  } = usePrimarySearchResults({
    input,
    isEmptyInputText: !barInputText.length,
    text: barInputText,
  });

  const containerRef = React.useRef<HTMLDivElement>(null);

  const width = useListenElementOffsetWidth(containerRef);

  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const [enableTippyAnimation, setEnableTippyAnimation] = React.useState(true);

  React.useEffect(() => {
    if (!searchInputRef.current) return;

    const searchRef = searchInputRef.current;

    const focus = () => {
      onSearchInputFocus?.();
      setEnableTippyAnimation(true);
    };

    searchRef.addEventListener('focus', focus);

    return () => {
      searchRef.removeEventListener('focus', focus);
    };
  }, [onSearchInputFocus, pathname]);

  React.useEffect(() => {
    if (!tablet) {
      ParamsStore.q = encodeURIComponent(barInputText);
    }

    if (!searchInputRef.current || !onSearchInputBlur) return;

    const searchRef = searchInputRef.current;

    const blur = () => {
      onSearchInputBlur();
    };

    searchRef.addEventListener('blur', blur);

    return () => {
      searchRef.removeEventListener('blur', blur);
    };
  }, [onSearchInputBlur, tablet, barInputText]);

  const onClearClick = useStableCallback(() => {
    clearText();
    extraClearSearch?.({
      inputRef: searchInputRef,
    });
  });

  const ClearSearch =
    hideClearOnEmpty && !barInputText ? null : (
      <ClearSearchButton onClick={onClearClick} aria-label="Clear search input">
        <FontAwesomeIcon icon={faXmark} size="lg" />
      </ClearSearchButton>
    );

  const onDesktopRowClick = useStableCallback(() => {
    close();
    focusOff();
    setEnableTippyAnimation(false);
    clearText();
  });

  const onSearchClick = useStableCallback(() => {
    close();
    focusOff();
    setEnableTippyAnimation(false);
  });

  const shouldRenderSearchResults = hasHadFocus || isSearchOpen;

  const isBlurBackground = BLUR_BACKGROUND_PATHS.includes(pathname);

  const searchElements = (
    <SearchBarContainer ref={containerRef}>
      <SearchInputContainer main isBlurBackground={isBlurBackground} isActive={focus}>
        {tablet && (
          <>
            <div className="search-icon">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </div>
          </>
        )}
        <SearchInput
          ref={searchInputRef}
          type="text"
          value={barInputText}
          placeholder="Search for anything on Sound"
          onChange={handleInputChange}
          onSelect={focusOn}
          onClick={focusOn}
          isBlurBackground={isBlurBackground}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onSearchClick();
              getSearchPageResults({ inputText: barInputText, useRouter: true });
            }
          }}
        />
        {ClearSearch}
      </SearchInputContainer>
    </SearchBarContainer>
  );

  return (
    <LazyTippy
      arrow={false}
      placement="bottom"
      visible={focus}
      onClickOutside={focusOff}
      interactive
      maxWidth="none"
      animation={enableTippyAnimation ? 'fade' : false}
      zIndex={parseInt(theme.zIndices.search.value, 10)}
      content={
        shouldRenderSearchResults && (
          <div
            style={{
              minWidth: width,
            }}
          >
            <SearchResultsWrapper>
              <SearchResults
                artistResults={artistResults}
                releaseResults={releaseResults}
                playlistResults={playlistResults}
                collectorResults={collectorResults}
                debouncedInputText={barSearchText}
                inputText={barInputText}
                isLoading={isLoading}
                onRowClick={onDesktopRowClick}
                onSearchClick={onSearchClick}
                searchText={barInputText}
              />
            </SearchResultsWrapper>
          </div>
        )
      }
    >
      {searchElements}
    </LazyTippy>
  );
}

const SearchResultsWrapper = styled('div', {
  overflow: 'hidden',
  background: '$white',
  boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.18)',
  borderRadius: 8,
});
